import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Input, InputNumber, Form, Row, Col, Button } from 'antd';
import { hotkeys } from '@ohif/core';
import { MODIFIER_KEYS } from './hotkeysConfig';
import { useSnackbarContext } from '@ohif/ui';
import { hotkeysManager } from '../../App';
import store from './../../store';
// import { hotkeys } from '@ohif/core';
import { useTranslation } from 'react-i18next';
import './MarkPreferences.styl';

/**
 * HotkeysPreferences tab
 * It renders all hotkeys displayed into columns/rows
 *
 * It stores current state and whenever it changes, component messages parent of new value (through function callback)
 * @param {object} props component props
 * @param {string} props.onClose
 */
function MarkPreferences({ onClose }) {
  const { t } = useTranslation('UserPreferencesModal');
  let spacing = 1;
  let doubleSpacing = 4;
  let markHotkeys = {
    markNextImage: { keys: ['down'], label: 'Next Image', type: 'mark' },
    markPreviousImage: {
      keys: ['up'],
      label: 'Previous Image',
      type: 'mark',
    },
    markScaleDownViewport: { keys: ['-'], label: 'Zoom Out', type: 'mark' }, //缩小
    markScaleUpViewport: { keys: ['+'], label: 'Zoom In', type: 'mark' }, //放大
    markWindowLevelPreset1: { keys: ['1'], label: 'W/L W+', type: 'mark' }, //层级W增加
    markWindowLevelPreset2: { keys: ['2'], label: 'W/L W-', type: 'mark' }, //层级W减小
    markWindowLevelPreset3: { keys: ['3'], label: 'W/L L+', type: 'mark' }, //层级L增加
    markWindowLevelPreset4: { keys: ['4'], label: 'W/L L+', type: 'mark' }, //层级L减小
  };
  try {
    spacing = localStorage.getItem('freehand-spacing') || spacing;
    doubleSpacing =
      localStorage.getItem('freehand-doubleSpacing') || doubleSpacing;
    markHotkeys =
      JSON.parse(localStorage.getItem('hotkey-mark-definitions')) ||
      markHotkeys;
  } catch (error) {
    console.log('自定义标注热键获取失败');
  }
  const [hotkeyMarkDefinitions, setHotkeyMarkDefinitions] = useState(
    markHotkeys
  );
  const snackbar = useSnackbarContext();
  const [form] = Form.useForm();

  const onFinish = values => {
    hotkeyMarkDefinitions.markNextImage.keys = values.next;
    hotkeyMarkDefinitions.markPreviousImage.keys = values.previous;
    hotkeyMarkDefinitions.markScaleUpViewport.keys = values.zoomIn;
    hotkeyMarkDefinitions.markScaleDownViewport.keys = values.zoomOut;
    hotkeyMarkDefinitions.markWindowLevelPreset1.keys = values.LWIncrease;
    hotkeyMarkDefinitions.markWindowLevelPreset2.keys = values.LWReduce;
    hotkeyMarkDefinitions.markWindowLevelPreset3.keys = values.LLIncrease;
    hotkeyMarkDefinitions.markWindowLevelPreset4.keys = values.LLReduce;
    const { viewer } = store.getState();
    if (
      viewer.activeButtonIds.length &&
      (viewer.activeButtonIds.indexOf('freehand') !== -1 ||
        viewer.activeButtonIds.indexOf('rect') !== -1)
    ) {
      hotkeysManager.setHotkeys(hotkeyMarkDefinitions);
    }
    localStorage.setItem('freehand-spacing', values.spacing);
    localStorage.setItem('freehand-doubleSpacing', values.doubleSpacing);
    localStorage.setItem(
      'hotkey-mark-definitions',
      JSON.stringify(hotkeyMarkDefinitions)
    );
    onClose();

    snackbar.show({
      message: t('保存成功'),
      type: 'success',
    });
  };

  const onResetPreferences = () => {
    setHotkeyMarkDefinitions(markHotkeys);
  };

  // const [helpMsg, setHelpMsg] = useState(null);

  const validateStatus = (value, attribute) => {
    let msg = '';
    const obj = form.getFieldsValue();
    delete obj['spacing'];
    delete obj[attribute];
    for (let key in obj) {
      if (obj[key] === value || obj[key][0] === value[0]) {
        msg = `按键"${value}" 已经被使用`;
      }
    }
    // console.log(ss);
    return msg;
  };

  const getKeys = ({ sequence, modifier_keys }) => {
    const keysArray = sequence.join(' ').split('+');
    let keys = [];
    let modifiers = [];
    keysArray.forEach(key => {
      if (modifier_keys && modifier_keys.includes(key)) {
        modifiers.push(key);
      } else {
        keys.push(key);
      }
    });
    return [...modifiers, ...keys];
  };

  const onInputKeyDown = event => {
    event.stopPropagation();
    event.preventDefault();
    let keys = '';
    let id = event.target.id;
    hotkeys.record(sequence => {
      keys = getKeys({ sequence, MODIFIER_KEYS });
      hotkeys.unpause();
      switch (id) {
        case 'previous':
          form.setFieldsValue({ previous: keys });
          form.validateFields(['previous']);
          return;
        case 'next':
          form.setFieldsValue({ next: keys });
          form.validateFields(['next']);
          return;
        case 'zoomIn':
          form.setFieldsValue({ zoomIn: keys });
          form.validateFields(['zoomIn']);
          return;
        case 'zoomOut':
          form.setFieldsValue({ zoomOut: keys });
          form.validateFields(['zoomOut']);
          return;
        case 'LWIncrease':
          form.setFieldsValue({ LWIncrease: keys });
          form.validateFields(['LWIncrease']);
          return;
        case 'LWReduce':
          form.setFieldsValue({ LWReduce: keys });
          form.validateFields(['LWReduce']);
          return;
        case 'LLIncrease':
          form.setFieldsValue({ LLIncrease: keys });
          form.validateFields(['LLIncrease']);
          return;
        case 'LLReduce':
          form.setFieldsValue({ LLReduce: keys });
          form.validateFields(['LLReduce']);
          return;
      }
    });
  };

  const onFocus = () => {
    hotkeys.pause();
    hotkeys.startRecording();
  };

  const formItemLayout = {
    labelAlign: 'right',
    labelCol: {
      span: 6,
    },
    wrapperCol: {
      span: 12,
    },
  };
  return (
    <React.Fragment>
      <div className="MarkPreferences">
        <Form
          form={form}
          {...formItemLayout}
          initialValues={{
            spacing: spacing,
            doubleSpacing: doubleSpacing,
            next: hotkeyMarkDefinitions.markNextImage.keys,
            previous: hotkeyMarkDefinitions.markPreviousImage.keys,
            zoomIn: hotkeyMarkDefinitions.markScaleUpViewport.keys,
            zoomOut: hotkeyMarkDefinitions.markScaleDownViewport.keys,
            LWIncrease: hotkeyMarkDefinitions.markWindowLevelPreset1.keys,
            LWReduce: hotkeyMarkDefinitions.markWindowLevelPreset2.keys,
            LLIncrease: hotkeyMarkDefinitions.markWindowLevelPreset3.keys,
            LLReduce: hotkeyMarkDefinitions.markWindowLevelPreset4.keys,
          }}
          onFinish={onFinish}
        >
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item label="多边形普通标注间距" name="spacing">
                <InputNumber
                  min={1}
                  max={10}
                  // defaultValue={4}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="多边形精细标注间距" name="doubleSpacing">
                <InputNumber
                  min={1}
                  max={10}
                  // defaultValue={4}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label="快捷按键设置："
                style={{
                  marginBottom: '10px',
                }}
              ></Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col span={12}>
              {/* <Form.Item label="画笔间距" name="next">
                <InputNumber min={1} max={10} defaultValue={4} style={{ width: '100%' }} />
              </Form.Item> */}
              <Form.Item
                label="上一张"
                name="previous"
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      let msg = validateStatus(value, 'previous');
                      if (msg) {
                        return Promise.reject(new Error(msg));
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <Input
                  prefix={'command:'}
                  placeholder="placeholder"
                  readOnly={true}
                  type="text"
                  onKeyDown={onInputKeyDown}
                  onFocus={onFocus}
                />
              </Form.Item>
              <Form.Item
                label="下一张"
                name="next"
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      let msg = validateStatus(value, 'next');
                      if (msg) {
                        return Promise.reject(new Error(msg));
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <Input
                  prefix={'command:'}
                  placeholder="placeholder"
                  readOnly={true}
                  type="text"
                  onKeyDown={onInputKeyDown}
                  onFocus={onFocus}
                />
              </Form.Item>
              <Form.Item
                label="放  大"
                name="zoomIn"
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      let msg = validateStatus(value, 'zoomIn');
                      if (msg) {
                        return Promise.reject(new Error(msg));
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <Input
                  prefix={'command:'}
                  placeholder="placeholder"
                  readOnly={true}
                  type="text"
                  onKeyDown={onInputKeyDown}
                  onFocus={onFocus}
                />
              </Form.Item>
              <Form.Item
                label="缩  小"
                name="zoomOut"
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      let msg = validateStatus(value, 'zoomOut');
                      if (msg) {
                        return Promise.reject(new Error(msg));
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <Input
                  prefix={'command:'}
                  placeholder="placeholder"
                  readOnly={true}
                  type="text"
                  onKeyDown={onInputKeyDown}
                  onFocus={onFocus}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Leave-W +"
                name="LWIncrease"
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      let msg = validateStatus(value, 'LWIncrease');
                      if (msg) {
                        return Promise.reject(new Error(msg));
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <Input
                  prefix={'command:'}
                  placeholder="placeholder"
                  readOnly={true}
                  type="text"
                  onKeyDown={onInputKeyDown}
                  onFocus={onFocus}
                />
              </Form.Item>
              <Form.Item
                label="Leave-W  -"
                name="LWReduce"
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      let msg = validateStatus(value, 'LWReduce');
                      if (msg) {
                        return Promise.reject(new Error(msg));
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <Input
                  prefix={'command:'}
                  placeholder="placeholder"
                  readOnly={true}
                  type="text"
                  onKeyDown={onInputKeyDown}
                  onFocus={onFocus}
                />
              </Form.Item>
              <Form.Item
                label="Leave-L  +"
                name="LLIncrease"
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      let msg = validateStatus(value, 'LLIncrease');
                      if (msg) {
                        return Promise.reject(new Error(msg));
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <Input
                  prefix={'command:'}
                  placeholder="placeholder"
                  readOnly={true}
                  type="text"
                  onKeyDown={onInputKeyDown}
                  onFocus={onFocus}
                />
              </Form.Item>
              <Form.Item
                label="Leave-L -"
                name="LLReduce"
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      let msg = validateStatus(value, 'LLReduce');
                      if (msg) {
                        return Promise.reject(new Error(msg));
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <Input
                  prefix={'command:'}
                  placeholder="placeholder"
                  readOnly={true}
                  type="text"
                  onKeyDown={onInputKeyDown}
                  onFocus={onFocus}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row
            gutter={24}
            style={{
              borderTop: '3px solid var(--primary-background-color)',
              position: 'absolute',
              bottom: '0px',
              width: '100%',
              paddingTop: '20px',
            }}
          >
            <Col span={12} style={{ textAlign: 'left' }}>
              <Form.Item>
                <Button
                  type="primary"
                  htmlType="button"
                  onClick={onResetPreferences}
                  danger
                >
                  返回默认
                </Button>
              </Form.Item>
            </Col>
            <Col span={12} style={{ textAlign: 'right' }}>
              <Button htmlType="button" onClick={onClose}>
                取消
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                style={{
                  marginLeft: '10px',
                }}
              >
                保存
              </Button>
            </Col>
          </Row>
        </Form>
      </div>
    </React.Fragment>
  );
}

MarkPreferences.propTypes = {
  onClose: PropTypes.func,
};

export { MarkPreferences };
